import {
  Badge, IconButton, IconButtonProps,
} from '@mui/material';
import { BadgeOrigin } from '@mui/material/Badge/Badge';
import {
  AB1,
  AB3,
  S2,
  S3,
  S4,
} from '../../theme';
import { mergeSX } from '../../services/helpers';

interface IBuildSXParams {
  background: string;
  activeBackground: string;
  hoverBackground: string;
  activeColor: string;
  hoverColor: string;
  color: string;
  width: number | string;
  height: number | string;
  padding: number | string;
  disabledColor: string;
  borderBottomLeftRadius?: string | number;
  borderTopLeftRadius?: string | number;
  hoverBorderColor?: string;
  activeBorderColor: string;
}

interface IBuildBadgeSXParams {
  notificationColor: string;
  hideBadgeBorder: boolean;
  notificationOutlineColor: string;
  badgeBottom?: string | number;
  badgeRight?: string | number;
  badgeMinWidth?: string | number;
  badgeHeight?: string | number;
}

const buildSX = ({
  activeBackground,
  hoverBackground,
  activeColor,
  hoverColor,
  background,
  color,
  width,
  height,
  padding,
  disabledColor,
  borderBottomLeftRadius,
  borderTopLeftRadius,
  hoverBorderColor,
  activeBorderColor,
}: IBuildSXParams) => ({
  '&.MuiIconButton-root': {
    background,
    width,
    height,
    color,
    padding,
    borderBottomLeftRadius,
    borderTopLeftRadius,
    '&.Mui-disabled': {
      background,
      color: disabledColor,
    },
  },
  '&:hover': {
    color: hoverColor,
    background: hoverBackground,
    border: hoverBorderColor ? `1px solid ${hoverBorderColor}` : null,
  },
  '&:active': {
    background: activeBackground,
    color: activeColor,
    border: `1px solid ${activeBorderColor}`,
  },
});

const buildBadgeSX = ({
  notificationColor,
  hideBadgeBorder,
  notificationOutlineColor,
  badgeBottom,
  badgeRight,
  badgeMinWidth,
  badgeHeight,
}: IBuildBadgeSXParams) => ({
  '& .MuiBadge-badge': {
    backgroundColor: notificationColor,
    border: hideBadgeBorder ? 'none' : `1px solid ${notificationOutlineColor}`,
    bottom: badgeBottom,
    right: badgeRight,
    minWidth: badgeMinWidth,
    height: badgeHeight,
  },
});

export interface IActionButtonProps extends Omit<
  IconButtonProps, 'color'
>, Partial<IBuildSXParams> {
  hasNotification?: boolean;
  notificationOutlineColor?: string;
  notificationColor?: string;
  hideBadgeBorder?: boolean;
  badgeBottom?: string | number;
  badgeRight?: string | number;
  badgeMinWidth?: string | number;
  badgeHeight?: string | number;
  borderBottomLeftRadius?: string | number;
  borderTopLeftRadius?: string | number;
}

const ANCHOR_ORIGIN: BadgeOrigin = { vertical: 'bottom', horizontal: 'right' };

const ActionButton = (props: IActionButtonProps) => {
  const {
    sx,
    children,
    color = S4,
    background = S2,
    activeBackground = AB1,
    hoverBackground = S2,
    activeColor = AB3,
    hoverColor = AB3,
    width = 34,
    height = 34,
    padding = 1,
    disabledColor = S3,
    hasNotification,
    notificationOutlineColor = 'inherit',
    notificationColor = background,
    hideBadgeBorder = false,
    badgeBottom,
    badgeRight,
    badgeMinWidth,
    badgeHeight,
    borderBottomLeftRadius,
    borderTopLeftRadius,
    hoverBorderColor,
    activeBorderColor = activeColor,
    ...restProps
  } = props;
  return (
    <Badge
      invisible={!hasNotification}
      variant="dot"
      overlap="circular"
      anchorOrigin={ANCHOR_ORIGIN}
      sx={buildBadgeSX({
        notificationColor,
        hideBadgeBorder,
        notificationOutlineColor,
        badgeBottom,
        badgeRight,
        badgeMinWidth,
        badgeHeight,
      })}
    >
      <IconButton
        sx={mergeSX(buildSX({
          background,
          activeBackground,
          hoverBackground,
          activeColor,
          hoverColor,
          color,
          width,
          height,
          padding,
          disabledColor,
          borderBottomLeftRadius,
          borderTopLeftRadius,
          hoverBorderColor,
          activeBorderColor,
        }), sx)}
        {...restProps}
      >
        {children}
      </IconButton>
    </Badge>
  );
};

export default ActionButton;
